import React from "react";
import {
  Button,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
} from "@mui/material";
import {
  Article as ArticleIcon,
  CloudUpload as CloudUploadIcon,
  Link as LinkIcon,
  MergeType as MergeTypeIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  StarBorder as StarIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import MultiSearchInput from "./MultiSearchInput";

export default function AssessmentForm({
  formData,
  handleChangeInputAssessment,
  handleInsertAssessment,
  isEditing,
  handleUpdateAssessment,
  upLoadImageAssessment,
  imageRefDoc,
  childExamIDs,
  setChildExamIDs,
  selectedExams,
  setSelectedExams,
}) {
  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "16px",
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(10px)",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 1)",
        transform: "translateY(-2px)",
        boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
      },
      "&.Mui-focused": {
        backgroundColor: "rgba(255, 255, 255, 1)",
        boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.2)",
        transform: "translateY(-2px)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#6366f1",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      fontWeight: 600,
      color: "#4B5563",
      "&.Mui-focused": {
        color: "#6366f1",
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Card
        elevation={0}
        sx={{
          maxWidth: "1200px",
          margin: "0 auto",
          borderRadius: "32px",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          overflow: "visible",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-2px",
            left: "-2px",
            right: "-2px",
            bottom: "-2px",
            background: "linear-gradient(45deg, #667eea, #764ba2, #667eea)",
            borderRadius: "32px",
            zIndex: -1,
            opacity: 0.1,
          },
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4, md: 6 } }}>
          {/* Header Section */}
          <Box textAlign="center" mb={6}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 80,
                height: 80,
                borderRadius: "24px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                mb: 3,
                boxShadow: "0 20px 40px rgba(102, 126, 234, 0.3)",
              }}
            >
              <StarIcon sx={{ fontSize: 40, color: "white" }} />
            </Box>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              }}
            >
              {isEditing ? "Cập nhật Bài thi" : "Tạo Bài Thi Mới"}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#6B7280",
                fontSize: { xs: "1rem", sm: "1.1rem" },
                maxWidth: "600px",
                margin: "0 auto",
                lineHeight: 1.6,
              }}
            >
              Điền nhanh các thông tin bên dưới để khởi tạo bài thi của bạn. Hệ
              thống sẽ tự động xử lý và tối ưu hóa nội dung.
            </Typography>
          </Box>

          {/* Form Fields */}
          <Stack spacing={4}>
            {/* Row 1: Title & Type */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: { xs: 3, md: 4 },
              }}
            >
              <TextField
                label="Tên bài thi"
                placeholder="Nhập tên bài thi..."
                name="title"
                value={formData?.title || ""}
                onChange={handleChangeInputAssessment}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ArticleIcon sx={{ color: "#667eea" }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputStyle}
              />

              <TextField
                label="Kì thi"
                name="type"
                value={formData?.type || ""}
                onChange={handleChangeInputAssessment}
                select
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MergeTypeIcon sx={{ color: "#764ba2" }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputStyle}
              >
                {["TSA", "THPT"].map((option) => (
                  <MenuItem key={option} value={option}>
                    <Chip
                      label={option}
                      size="small"
                      sx={{
                        background:
                          option === "TSA"
                            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                            : "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Access Control */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: { xs: 3, md: 4 },
              }}
            >
              <TextField
                label="Quyền truy cập"
                name="access"
                value={formData?.access || ""}
                onChange={handleChangeInputAssessment}
                select
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SecurityIcon sx={{ color: "#10B981" }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputStyle}
              >
                {["PUBLIC", "PRIVATE"].map((option) => (
                  <MenuItem key={option} value={option}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          background:
                            option === "PUBLIC"
                              ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                              : "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                        }}
                      />
                      <Typography fontWeight={600}>{option}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Loại bài thi *"
                fullWidth
                select
                name="typeExam"
                value={formData?.typeExam}
                onChange={handleChangeInputAssessment}
                variant="outlined"
                size="medium"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "white",
                    "&:hover fieldset": {
                      borderColor: "#3b82f6",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                    },
                  },
                }}
              >
                {["ĐỀ THI THỬ", "BÀI TẬP"].map((option, key) => (
                  <MenuItem
                    key={key}
                    value={option}
                    className="hover:bg-blue-50"
                  >
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: { xs: 3, md: 4 },
              }}
            >
              <TextField
                label="Môn học (Không bắt buộc đối với loại bài thi ĐỀ THI THỬ)"
                fullWidth
                select
                name="subject"
                value={formData?.subject}
                onChange={handleChangeInputAssessment}
                variant="outlined"
                size="medium"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "white",
                    "&:hover fieldset": {
                      borderColor: "#3b82f6",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                    },
                  },
                }}
              >
                {[
                  "TƯ DUY TOÁN HỌC",
                  "TƯ DUY ĐỌC HIỂU",
                  "TƯ DUY KHOA HỌC",
                  "TOÁN HỌC",
                  "ĐỌC HIỂU",
                  "KHOA HỌC",
                  "VẬT LÝ",
                  "HÓA HỌC",
                  "SINH HỌC",
                  "TIẾNG ANH",
                  "ĐỊA LÝ",
                  "LỊCH SỬ",
                ].map((option, key) => (
                  <MenuItem
                    key={key}
                    value={option}
                    className="hover:bg-blue-50"
                  >
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Search Section */}
            <Card
              sx={{
                borderRadius: "24px",
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
                border: "2px dashed rgba(102, 126, 234, 0.2)",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "rgba(102, 126, 234, 0.4)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 35px rgba(102, 126, 234, 0.15)",
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "16px",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <LinkIcon sx={{ color: "white", fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#374151",
                        mb: 0.5,
                      }}
                    >
                      Tìm kiếm và thêm đề thi
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6B7280",
                      }}
                    >
                      Tìm và chọn các đề thi phù hợp để tạo bài thi tổng hợp
                    </Typography>
                  </Box>
                </Box>

                <MultiSearchInput
                  childExamIDs={childExamIDs}
                  setChildExamIDs={setChildExamIDs}
                  selectedExams={selectedExams}
                  setSelectedExams={setSelectedExams}
                />

                <Box
                  sx={{
                    mt: 3,
                    p: 3,
                    borderRadius: "16px",
                    background: "rgba(102, 126, 234, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      background:
                        "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "white",
                        fontWeight: 700,
                        fontSize: "1.2rem",
                      }}
                    >
                      {selectedExams?.length || childExamIDs?.length || 0}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: "#374151",
                      fontWeight: 600,
                    }}
                  >
                    đề thi đã được chọn
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 3,
                mt: 4,
                pt: 4,
                borderTop: "1px solid rgba(0, 0, 0, 0.1)",
              }}
            >
              {isEditing ? (
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleUpdateAssessment}
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    px: 6,
                    py: 2,
                    borderRadius: "20px",
                    background:
                      "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    textTransform: "none",
                    boxShadow: "0 12px 35px rgba(245, 158, 11, 0.4)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #D97706 0%, #B45309 100%)",
                      transform: "translateY(-3px)",
                      boxShadow: "0 20px 45px rgba(245, 158, 11, 0.5)",
                    },
                  }}
                >
                  Cập nhật Bài thi
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleInsertAssessment}
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    px: 6,
                    py: 2,
                    borderRadius: "20px",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    textTransform: "none",
                    boxShadow: "0 12px 35px rgba(102, 126, 234, 0.4)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                      transform: "translateY(-3px)",
                      boxShadow: "0 20px 45px rgba(102, 126, 234, 0.5)",
                    },
                  }}
                >
                  Tạo Bài Thi
                </Button>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
